/**
 * THE TURN TIMELINE. This component is the deliverable.
 *
 * Everything else in this app is plumbing that exists so that this view can be
 * opened on the worst call of the day and answer, out loud, which party was slow
 * and for how long. So the shape of it is argued rather than styled:
 *
 * ONE ROW PER TURN, AND THE ROWS SHARE A SCALE. Each bar is drawn as a fraction
 * of the SLOWEST turn in the session, not stretched to fill its row. A per-row
 * normalisation makes every turn look equally bad and destroys the only
 * comparison a reader actually wants to make, which is between turns.
 *
 * EVERY SEGMENT HAS ITS OWN HUE, AND INFERENCE HAS THE STRONGEST. "The provider
 * was slow" is the conclusion this view exists to let somebody reach, so its
 * blue is the most saturated thing in the bar. The rest used to be a greyscale
 * ramp, which read as principled and was actually unreadable: a 3% admission
 * sliver and a 3% VAD sliver were the same grey, and grey-on-grey at five pixels
 * wide is invisible. So: VAD teal, admission violet, network amber (nobody's
 * fault, but fold it into inference and every congested Wi-Fi connection in the
 * world becomes an OpenAI latency complaint), tool magenta. The `--seg-*`
 * variables in index.css are the palette; the legend renders from the same
 * SEGMENTS array so the two cannot disagree.
 *
 * THE SEGMENTS ARE CAUSAL, NOT ARBITRARY, with one honest exception. VAD wait,
 * admission, inference and network really do happen one after the other and
 * really do sum to time-to-first-audio. TOOL TIME DOES NOT: a tool runs INSIDE
 * the response window, so `toolMs` overlaps `inferenceMs` rather than following
 * it. It is drawn last anyway, because a two-second tool is the single most
 * common cause of a slow turn in this system and a segment nobody can see is a
 * segment nobody fixes. The legend says so in as many words, and the row total
 * is time-to-first-audio, NOT the sum of the drawn widths.
 *
 * EVERY SEGMENT NAMES ITS OBSERVER. There is no provider-side wall clock
 * anywhere in the Realtime API, so each of these numbers is an interval between
 * two events as seen by one of our components, and which component it was
 * decides how much to believe it. `inferenceMs` is the strongest number here —
 * both ends observed on the sideband, in our datacenter, with no client involved.
 * `networkMs` is the weakest — it spans two clocks reconciled by an NTP-style
 * exchange over the control socket. A reader has to be able to tell those apart,
 * so hovering any segment states its observer alongside its milliseconds.
 *
 * ⚠️ AND NAMING THE OBSERVER IS NOT ENOUGH FOR INFERENCE. The far end of that
 * interval, `output_audio_buffer.started`, is not a generation event: it fires
 * when a response's audio begins going out on the wire, and responses share one
 * serialized playout queue. A response created while the previous one is still
 * playing therefore gets its `started` at the moment that playout DRAINS — so the
 * interval is real, measured by one observer, and mostly not the provider.
 *
 * This screen was reporting "slowest provider inference was turn 4 at 3318 ms" on
 * session 868a0c2d, where most of that interval was our own playout queue and the
 * provider's own share is unmeasurable. That is the wrong answer in the one place this product
 * claims to be right, so `audioBufferBusyAtCreate` is read before `inferenceMs`
 * everywhere below: contaminated turns are drawn differently, labelled, and
 * excluded from both the median and the "slowest" claim. See `inferenceQuality`.
 *
 * WHY A HOVER READOUT AND NOT 5N TOOLTIPS. Thirty turns is a hundred and fifty
 * hoverable segments. Radix tooltips would be a hundred and fifty portals and a
 * strobe as the pointer sweeps the bar. Instead every segment carries a native
 * `title` for the patient reader, and moving over one writes a single fixed
 * readout line above the rows — which has the better property anyway: it never
 * covers the bar it describes.
 */
import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { TurnRow } from '@/api'
import { fmtMs } from '@/components/state-pill'

type SegKey = 'vad' | 'admission' | 'inference' | 'network' | 'tool'

interface SegSpec {
  key: SegKey
  label: string
  /** Which of OUR components measured it, in the words a reader needs. */
  observer: string
  /** What the two anchors actually are. */
  anchors: string
  className?: string
  style?: React.CSSProperties
  value: (t: TurnRow) => number | null
}

/** The five intervals, in causal order. Each carries a `.seg-*` class from
 *  index.css; the legend renders from this array, so bar and legend agree. */
const SEGMENTS: SegSpec[] = [
  {
    key: 'vad',
    label: 'VAD wait',
    observer: 'the browser’s own VAD, reconciled against the DO',
    anchors: 'local speech start → input_audio_buffer.speech_stopped',
    className: 'seg-vad',
    value: (t) => t.vadWaitMs,
  },
  {
    key: 'admission',
    label: 'admission',
    observer: 'the Durable Object alone',
    anchors: 'input_audio_buffer.speech_stopped → response.created',
    className: 'seg-admission',
    value: (t) => t.admissionMs,
  },
  {
    key: 'inference',
    label: 'inference',
    observer: 'the Durable Object alone — THE PROVIDER, on our clock',
    anchors: 'response.created → output_audio_buffer.started',
    className: 'seg-inference',
    value: (t) => t.inferenceMs,
  },
  {
    key: 'network',
    label: 'network',
    observer: 'both ends, across a reconciled clock — the weakest number here',
    anchors: 'output_audio_buffer.started → the browser’s first playout of that response',
    className: 'seg-network',
    value: (t) => t.networkMs,
  },
  {
    key: 'tool',
    label: 'tool',
    observer: 'the Durable Object alone',
    anchors: 'response.function_call_arguments.done → conversation.item.create(function_call_output)',
    className: 'seg-tool',
    value: (t) => t.toolMs,
  },
]

/** Sum of the drawn widths. NOT the same as time-to-first-audio — see the header. */
function drawnTotal(t: TurnRow): number {
  let sum = 0
  for (const s of SEGMENTS) sum += s.value(t) ?? 0
  return sum
}

/**
 * WHAT THIS TURN'S `inferenceMs` ACTUALLY IS. Three answers, and they are not
 * interchangeable.
 *
 *   clean         the buffer was OBSERVED IDLE at `response.created`, so the
 *                 interval is the provider and nothing else
 *   contaminated  the buffer was OBSERVED BUSY, so the interval contains the
 *                 previous response's remaining playout — most of it, typically
 *   unobserved    nobody checked. Every turn recorded before the Durable Object
 *                 started tracking the buffer (all 60 turns of the nine real
 *                 sessions) is in this state, as is any turn whose
 *                 `response.created` arrived while our sideband was detached.
 *
 * `unobserved` IS NOT `clean` AND IT IS NOT `contaminated`. It is deliberately
 * left in the median, because excluding it would empty the panel for every call
 * recorded so far — but the footer says how many of the turns behind that median
 * were never checked, which is the difference between a caveat and a lie.
 */
type InferenceQuality = 'clean' | 'contaminated' | 'unobserved'

function inferenceQuality(t: TurnRow): InferenceQuality {
  if (t.audioBufferBusyAtCreate === true) return 'contaminated'
  if (t.audioBufferBusyAtCreate === false) return 'clean'
  return 'unobserved'
}

/**
 * How each of the three states is drawn and what is said about it.
 *
 * THE COLOUR ARGUMENT MATTERS HERE. `.seg-inference` is the most saturated hue
 * in this view precisely because "the provider was slow" is the conclusion the
 * eye should reach first — so a contaminated interval must not wear it. It is
 * drawn as a grey hatch, the one grey left in the bar, because what it mostly
 * measures is our own playout queue and it must not read as any of the named
 * segments. The unobserved state keeps the provider hue, hatched against a pale
 * tint: it may well be provider latency, and we cannot say.
 *
 * `style: null` means "leave `.seg-inference` alone" — an inline background would
 * override the class, and the clean case is exactly what the class is for.
 */
const INFERENCE_QUALITY: Record<
  InferenceQuality,
  {
    /** Marker for the row's Notes column. Null on the ordinary case. */
    pill: string | null
    /**
     * Composed from the existing theme utilities rather than a new `.pill-*`
     * rule, because the stylesheet's own convention is worth following exactly:
     * SOLID AMBER for a measured problem, DASHED for "could not measure". That
     * is the `PARTIAL` / `UNVERIFIED` distinction the evidence pills already
     * make, and it survives a monochrome screenshot, which a hue does not.
     */
    pillClass: string
    style: React.CSSProperties | null
    /** Said in the hover readout, the segment title, and the Notes tooltip. */
    caveat: string
  }
> = {
  clean: {
    pill: null,
    pillClass: '',
    style: null,
    caveat:
      'The output audio buffer was observed IDLE when this response was created, '
      + 'so this interval is provider latency.',
  },
  contaminated: {
    pill: 'includes playout',
    pillClass: 'border-ev-warn-border bg-ev-warn-bg font-semibold text-ev-warn',
    style: {
      background:
        'repeating-linear-gradient(135deg, var(--chart-3) 0 3px, var(--chart-1) 3px 6px)',
    },
    caveat:
      'NOT provider latency. The output audio buffer was still draining the previous '
      + 'response when this one was created, and output_audio_buffer.started cannot fire '
      + 'until that playout finishes — so most of this interval is the previous answer '
      + 'still being spoken. The provider’s own share is unmeasurable on this turn.',
  },
  unobserved: {
    pill: 'buffer unchecked',
    pillClass: 'border border-dashed border-border bg-muted text-muted-foreground',
    style: {
      background:
        'repeating-linear-gradient(135deg, var(--seg-inference) 0 5px, var(--ev-info-bg) 5px 10px)',
    },
    caveat:
      'The buffer state at response.created was never recorded for this turn, so we '
      + 'cannot say whether this is provider latency or the previous response’s playout. '
      + 'Treat it as an upper bound on the provider.',
  },
}

/** Does this turn's `inferenceMs` belong in a statement about the provider?
 *  Contaminated turns do not, at any strength. See the header. */
function countsAsProviderInference(t: TurnRow): boolean {
  return t.inferenceMs != null && inferenceQuality(t) !== 'contaminated'
}

/**
 * WHY THIS TURN HAS NO PROVIDER LATENCY, said correctly.
 *
 * The row used to print one sentence for all of these: "no anchors recorded — the
 * sideband was most likely detached for this turn". Checked against all seven real
 * sessions, that sentence is FALSE on all ten turns it renders on. Every one of
 * them has `sideband_reattaches = 0`, a `response_created_at` and (bar one) a
 * `response_done_at` — the sideband was attached the whole time and anchors WERE
 * recorded, just not the two that make a bar. Four distinct causes were collapsed
 * into one wrong guess, and they are not interchangeable:
 *
 *  1. THE ASSISTANT PRODUCED NOTHING. `audio_output_tokens = 0`, cancelled by
 *     `turn_detected` — the caller started talking before it spoke. Eight of the
 *     ten. There is no latency to report and inventing one would be worse.
 *
 *  2. THE ASSISTANT SPOKE AND THE PROVIDER SENT NO BUFFER EVENT. Session
 *     a0609ada turn 5: 130 audio tokens (6.5 s), status `completed`, a full
 *     transcript that appears in the transcript panel below — and NOT ONE
 *     `output_audio_buffer.*` event for that `response_id` anywhere in the raw S3
 *     log. THE CAUSE IS NOW OBSERVED RATHER THAN DEDUCED: the previous response
 *     was still draining when this one was created, `started` is deferred until
 *     that playout ends, and the call ended first — so it never arrived at all.
 *     `4c339d9a` turn 9 is the second instance (155 tokens; its `stopped` DID
 *     arrive, 7.75 s after turn 8's drain point, which is its own token count to
 *     within 10 ms). Where `audioBufferBusyAtCreate` is true this row says so
 *     outright; where it is null the wording stays a description of what is
 *     missing rather than a claim about why.
 *
 *  3. NO `response.done` AT ALL — the call ended mid-response (a52aad62 turn 5).
 *
 *  4. GENUINELY NO ANCHORS. Only then is a detached sideband the likely story.
 *
 * The distinction is the whole product: case 1 is "nothing to measure", case 2 is
 * "the instrument was not fed", and only case 4 is "we were not there".
 */
function noInferenceReason(t: TurnRow): string | null {
  if (t.inferenceMs != null) return null
  if (t.responseCreatedAt == null) {
    return 'no anchors recorded at all — the sideband was most likely detached for this turn'
  }
  if (t.responseDoneAt == null) {
    return 'the response never completed for us — no response.done arrived, so nothing closed this turn'
  }
  if (t.audioOutputTokens === 0) {
    return 'the assistant produced no audio on this turn (0 audio tokens billed), so there is '
      + 'no provider latency to report'
  }
  if (t.audioOutputTokens == null) {
    return 'no token usage was reported for this turn, so we cannot say whether the assistant spoke'
  }
  const spoke = `the assistant DID speak (${t.audioOutputTokens} audio tokens, about `
    + `${Math.round((t.audioOutputTokens * 50) / 100) / 10}s)`
  if (t.audioBufferBusyAtCreate === true) {
    return `${spoke} but the output audio buffer was still draining the previous response when `
      + 'this one was created, so output_audio_buffer.started never arrived — the call ended '
      + 'before the queue did. Provider inference is unobtainable for this turn, and that is a '
      + 'property of the anchor, not a fault of the call.'
  }
  return `${spoke} but the provider sent no output_audio_buffer.started for it, so provider `
    + 'inference is unmeasurable here'
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const s = [...values].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  if (s.length % 2 === 1) return s[mid] ?? null
  const lo = s[mid - 1]
  const hi = s[mid]
  return lo === undefined || hi === undefined ? null : Math.round((lo + hi) / 2)
}

interface Hover {
  n: number
  seg: SegSpec
  ms: number
  /** Set only on the inference segment, and only when there is something a
   *  reader would act on: what this interval is when it is not provider time. */
  caveat: string | null
}

export function TurnTimeline({ turns }: { turns: TurnRow[] }) {
  const [hover, setHover] = useState<Hover | null>(null)

  if (turns.length === 0) {
    return (
      <p className="empty text-sm">
        No turns were recorded. Either the call never reached a first response, or the
        sideband never attached — in which case the conversation may well have happened and
        we simply did not see it.
      </p>
    )
  }

  // The shared scale. One millisecond floor so a session of all-null anchors
  // divides by something.
  const scale = Math.max(1, ...turns.map(drawnTotal))

  const firstAudio = turns
    .map((t) => t.totalToFirstAudioMs)
    .filter((v): v is number => v != null)

  /**
   * THE INFERENCE POPULATION, AND WHAT IS KEPT OUT OF IT.
   *
   * A turn whose buffer was observed BUSY at `response.created` is excluded from
   * every statement this panel makes about the provider — the median and the
   * "slowest" claim alike. Measured on session 868a0c2d: including them put the
   * median at 668 ms and named turn 4 as the worst turn of the call at 3318 ms,
   * where most of the interval was our own playout queue and the provider's own
   * share is unmeasurable. Excluded, the median is 559 ms over five turns and the slowest
   * genuine provider interval is turn 2 at 770 ms.
   *
   * Turns whose buffer state was never observed STAY IN. They are not evidence of
   * contamination, only of a missing check, and dropping them would blank this
   * panel for every call recorded before the column existed. The footer states
   * how many of them there are.
   */
  const usable = turns.filter(countsAsProviderInference)
  const inference = usable.map((t) => t.inferenceMs).filter((v): v is number => v != null)
  const excluded = turns.filter((t) => t.inferenceMs != null && inferenceQuality(t) === 'contaminated')
  const unchecked = usable.filter((t) => inferenceQuality(t) === 'unobserved')

  // SLOWEST BY PROVIDER INFERENCE, not by time-to-first-audio.
  //
  // It used to be the max over `totalToFirstAudioMs`, which is a strictly smaller
  // population: that anchor needs the browser to have reported its playout, and
  // provider inference needs only the DO. On a real 11-turn call that produced
  // "slowest was turn 8 at 1849ms" while turn 7 sat at 1926ms of provider
  // inference, unanchored and therefore invisible — so the screen named the wrong
  // turn as the worst, on the one panel whose job is to say who was slow.
  //
  // Inference is also the better headline for that question: it is the provider's
  // own contribution, measured by one observer, with no clock reconciliation in
  // it. NOW FOLDED OVER `usable` RATHER THAN OVER EVERY TURN, for the reason
  // above: a contaminated interval is very often the largest number on the page,
  // so an unfiltered max lands on one almost by construction.
  const slowest = usable.reduce<TurnRow | null>(
    (worst, t) =>
      t.inferenceMs == null
        ? worst
        : worst == null || (worst.inferenceMs ?? 0) < t.inferenceMs
          ? t
          : worst,
    null,
  )

  return (
    <div className="min-w-0">
      {/*
        The readout. FIXED HEIGHT whether or not anything is hovered, because a
        line that appears on hover reflows every row underneath it and the bar
        moves out from under the pointer.
      */}
      <div className="mb-2 flex h-6 items-center gap-2 text-xs">
        {hover ? (
          <>
            <span
              className={cn('inline-block size-3 shrink-0 rounded-sm', hover.seg.className)}
              style={hover.seg.style}
              aria-hidden
            />
            <span className="font-medium">turn {hover.n}</span>
            <span className="text-muted-foreground">{hover.seg.label}</span>
            <span className="num font-semibold">{fmtMs(hover.ms)}</span>
            {hover.caveat === null ? (
              <span className="text-muted-foreground">
                observed by {hover.seg.observer}
              </span>
            ) : (
              /* The caveat REPLACES the observer line rather than joining it.
                 "observed by the Durable Object alone — THE PROVIDER" next to a
                 number that is mostly our playout queue is the exact sentence
                 this change exists to delete. */
              <span className="truncate font-medium text-ev-warn" title={hover.caveat}>
                {hover.caveat}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground/70">
            Hover a segment for its duration and which of our components measured it.
          </span>
        )}
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: '3.5rem' }}>Turn</th>
              <th>Latency decomposition</th>
              <th style={{ width: '7rem' }} className="text-right">
                To first audio
              </th>
              <th style={{ width: '9rem' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {turns.map((t) => {
              const total = drawnTotal(t)
              /**
               * THE OLD GUARD WAS `total === 0`, AND A FEW MILLISECONDS OF ADMISSION
               * DEFEATED IT. Turn 2 of session 13fb7248 has `admission_ms = 8` and
               * nothing else, so it drew an 8/1052 = 0.76% sliver and skipped the
               * explanation — the exact "an empty row in a latency view reads as
               * zero latency" failure this branch exists to prevent, on the turns
               * where it matters most. The honest test is whether there is a
               * PROVIDER latency to show, which is `inferenceMs`.
               */
              const reason = noInferenceReason(t)
              return (
                <tr key={t.n}>
                  <td className="num align-middle">{t.n}</td>
                  <td className="align-middle">
                    {reason !== null ? (
                      <span className="empty text-xs">
                        {reason}
                        {/* The intervals that DO exist are still stated, as text
                            rather than as a sliver nobody can see or hover. */}
                        {total > 0 && (
                          <>
                            {' · measured: '}
                            {SEGMENTS.filter((s) => (s.value(t) ?? 0) > 0)
                              .map((s) => `${s.label} ${fmtMs(s.value(t))}`)
                              .join(', ')}
                          </>
                        )}
                      </span>
                    ) : total === 0 ? (
                      <span className="empty text-xs">
                        no anchors recorded — the sideband was most likely detached for this
                        turn
                      </span>
                    ) : (
                      <div
                        className="lane"
                        aria-label={SEGMENTS.filter((s) => s.value(t) != null)
                          .map((s) => `${s.label} ${s.value(t)} ms`)
                          .join(', ')}
                      >
                        {SEGMENTS.map((s) => {
                          const ms = s.value(t)
                          if (ms == null || ms <= 0) return null
                          /* The inference segment is the only one whose meaning
                             depends on the turn, so it is the only one that reads
                             the turn to decide how it looks. */
                          const q = s.key === 'inference' ? INFERENCE_QUALITY[inferenceQuality(t)] : null
                          const caveat = q?.pill == null ? null : q.caveat
                          return (
                            <span
                              key={s.key}
                              className={s.className}
                              style={{ ...s.style, ...q?.style, width: `${(ms / scale) * 100}%` }}
                              title={
                                `${s.label}: ${fmtMs(ms)}\n${s.anchors}\nobserved by ${s.observer}`
                                + (q === null ? '' : `\n\n${q.caveat}`)
                              }
                              onMouseEnter={() => setHover({ n: t.n, seg: s, ms, caveat })}
                              onMouseLeave={() => setHover(null)}
                            />
                          )
                        })}
                      </div>
                    )}
                  </td>
                  <td className="num align-middle">
                    {/*
                      THE STORED COLUMN, not the sum of the widths. `turns.total_to_first_audio_ms`
                      is written once when the turn closes, so a later change to
                      the formula cannot silently rewrite history — and it excludes
                      tool time, which the drawn bar includes.
                    */}
                    {fmtMs(t.totalToFirstAudioMs)}
                  </td>
                  {/* `flex-wrap` with a gap because this cell can now hold TWO
                      pills: a barged-in turn is very often also a continuation
                      (turns 4 and 6 of 868a0c2d are both), and abutting
                      inline-blocks rendered as "includes playoutbarged in". */}
                  <td className="align-middle text-xs">
                    <span className="flex flex-wrap items-baseline gap-1">
                    {/*
                      THE MARKER GOES WHERE THE READER IS ALREADY LOOKING FOR
                      CAVEATS, next to "barged in". Rendered whenever the buffer
                      state is anything other than observed-idle, INCLUDING on the
                      turns that have no bar at all — a NULL `inferenceMs` caused
                      by a draining buffer is the same finding as an inflated one,
                      and the row explaining itself in prose is exactly where a
                      reader will ask why.
                    */}
                    {(() => {
                      const q = INFERENCE_QUALITY[inferenceQuality(t)]
                      if (q.pill === null) return null
                      return (
                        <span className={cn('pill', q.pillClass)} title={q.caveat}>
                          {q.pill}
                        </span>
                      )
                    })()}
                    {t.bargedIn && (
                      <span
                        className="pill pill-plain"
                        title={
                          t.truncatedAtMs == null
                            ? 'The user interrupted this response.'
                            : `The user interrupted after ${t.truncatedAtMs} ms of assistant audio had played. `
                              + 'The provider billed the audio it generated, so token-derived '
                              + 'completeness over-counts this turn.'
                        }
                      >
                        barged in
                      </span>
                    )}
                    {t.responseId == null && !t.bargedIn && (
                      <span className="faint" title="No response id — nothing on the sideband to join this turn to.">
                        unjoined
                      </span>
                    )}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/*
        Computed from the rows above and from nothing else. Said out loud, because
        a reader who has to compute a median off a bar chart will not.
      */}
      <p className="pt-2 text-xs text-muted-foreground">
        {turns.length} turn{turns.length === 1 ? '' : 's'}
        {' · median time to first audio '}
        <strong className="font-semibold text-foreground">{fmtMs(median(firstAudio))}</strong>
        {' · median provider inference '}
        <strong className="font-semibold text-foreground">{fmtMs(median(inference))}</strong>
        {slowest && (
          <>
            {' · slowest provider inference was turn '}
            <strong className="font-semibold text-foreground">{slowest.n}</strong>
            {' at '}
            {fmtMs(slowest.inferenceMs)}
          </>
        )}
        {/*
          COUNTED PER MEASURE. This reported one number — the turns missing a
          first-audio anchor — and said they were "excluded from both medians".
          They are two different populations: the anchor needs the browser's
          playout report, inference needs only the DO, and on a real call that was
          5 turns versus 2. Stating one figure for both was wrong about its own
          arithmetic on the panel that exists to be trusted about arithmetic.
        */}
        {(firstAudio.length < turns.length || inference.length < turns.length) && (
          <>
            {' · medians are over '}
            {firstAudio.length} turn{firstAudio.length === 1 ? '' : 's'} for first audio and{' '}
            {inference.length} for inference, of {turns.length}
          </>
        )}
        .
        {/*
          THE EXCLUSIONS, NAMED AND COUNTED. Two different reasons, so two
          sentences: one for the intervals we measured and know not to be provider
          time, one for the intervals we never checked. Rolling them together
          would reproduce in miniature exactly the error above.
        */}
        {excluded.length > 0 && (
          <>
            {' '}
            <strong className="font-semibold text-ev-warn">
              {excluded.length} turn{excluded.length === 1 ? '' : 's'} ({excluded.map((t) => t.n).join(', ')})
              {excluded.length === 1 ? ' is' : ' are'} excluded from both figures.
            </strong>{' '}
            The output audio buffer was still draining the previous response when{' '}
            {excluded.length === 1 ? 'it was' : 'they were'} created, so{' '}
            <span className="mono">output_audio_buffer.started</span> could not fire until that
            playout finished — the recorded interval
            {excluded.length === 1 ? '' : 's'} (
            {excluded.map((t) => fmtMs(t.inferenceMs)).join(', ')}) {excluded.length === 1 ? 'is' : 'are'}{' '}
            real, but mostly the previous answer still being spoken rather than the provider
            thinking.
          </>
        )}
        {unchecked.length > 0 && (
          <>
            {' '}
            {unchecked.length === inference.length
              ? 'The buffer state at response.created was never recorded for any of these turns'
              : `${unchecked.length} of the ${inference.length} turn${inference.length === 1 ? '' : 's'} behind `
                + 'the inference median had no buffer-state check'}
            , so {unchecked.length === 1 ? 'that one' : 'those'} may contain the same playout
            contamination undetected. Treat the inference median as an upper bound on the
            provider for this call.
          </>
        )}
      </p>

      <TimelineLegend turns={turns} />
    </div>
  )
}

/**
 * The legend, which is really a table of provenance.
 *
 * It names the observer for every segment because that is the load-bearing fact:
 * two of these are measured entirely inside our datacenter with no client
 * involved, and one of them spans two machines whose clocks were reconciled by a
 * best-effort exchange. Presenting five numbers of that different a quality in
 * one bar without saying so would be the most misleading thing this app could do.
 */
/**
 * WHEN THE INFERENCE SEGMENT IS PROVIDER LATENCY AND WHEN IT IS NOT, with the
 * three states drawn in the same fills the bars use.
 *
 * The key is rendered from `INFERENCE_QUALITY` rather than restated, so a bar and
 * its legend entry cannot come to disagree about what a hatch means — and the
 * per-state counts come from the turns actually on screen, so a state that does
 * not occur on this call is visibly absent instead of implied.
 */
function InferenceCaveat({ turns }: { turns: readonly TurnRow[] }) {
  const withInterval = turns.filter((t) => t.inferenceMs != null)
  const count = (q: InferenceQuality) => withInterval.filter((t) => inferenceQuality(t) === q).length
  const rows: Array<{ q: InferenceQuality; text: string }> = [
    { q: 'clean', text: 'buffer observed IDLE — this is provider latency' },
    {
      q: 'contaminated',
      text:
        'buffer observed BUSY — the interval is mostly the previous response’s remaining '
        + 'playout, so it is excluded from the median and from “slowest” above',
    },
    { q: 'unobserved', text: 'buffer state never recorded — an upper bound on the provider' },
  ]
  return (
    <>
      {' '}
      <strong className="font-semibold text-foreground">
        This interval is provider latency ONLY when the output audio buffer was idle at{' '}
        <span className="mono">response.created</span>.
      </strong>{' '}
      <span className="mono">output_audio_buffer.started</span> is not a generation event: it fires
      when a response&rsquo;s audio starts going out on the wire, and responses share one playout
      queue, so a response created while the previous one is still speaking gets its{' '}
      <span className="mono">started</span> when that playout DRAINS. Measured on session 868a0c2d:
      3,318&nbsp;ms recorded on a turn whose provider share cannot be measured at all, on a
      call whose five idle-buffer turns median 559&nbsp;ms.
      <span className="mt-1 flex flex-col gap-0.5">
        {rows.map((r) => (
          <span key={r.q} className="flex items-start gap-2">
            <span
              className={cn('mt-0.5 inline-block size-3 shrink-0 rounded-sm', 'seg-inference')}
              style={INFERENCE_QUALITY[r.q].style ?? undefined}
              aria-hidden
            />
            <span className={count(r.q) === 0 ? 'faint' : undefined}>
              {r.text} — {count(r.q)} turn{count(r.q) === 1 ? '' : 's'} here
            </span>
          </span>
        ))}
      </span>
    </>
  )
}

export function TimelineLegend({ turns }: { turns: readonly TurnRow[] }) {
  return (
    <div className="mt-4 rounded-md border bg-muted/40 p-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        What each segment is, and who measured it
      </h3>
      <dl className="grid gap-x-6 gap-y-2 text-xs" style={{ gridTemplateColumns: 'auto 1fr' }}>
        {SEGMENTS.map((s) => {
          /**
           * A LEGEND ENTRY FOR A MEASUREMENT THAT WAS NEVER TAKEN reads as a
           * measurement that came out small. On all seven real sessions `vad` and
           * `network` are null on 43 of 43 turns — both need a client anchor, and
           * `client_first_playout_at` has never once been joined — so the legend
           * described two instruments this page has no readings from, with no
           * indication that the bars above contain none of either.
           */
          const measured = turns.filter((t) => s.value(t) != null).length
          return (
            <div key={s.key} className="contents">
              <dt className="flex items-center gap-2 whitespace-nowrap font-medium">
                <span
                  className={cn('inline-block size-3 shrink-0 rounded-sm', s.className)}
                  style={s.style}
                  aria-hidden
                />
                {s.label}
              </dt>
              <dd className="min-w-0 text-muted-foreground">
                <span className="mono">{s.anchors}</span>
                {' — observed by '}
                {s.observer}.
                {/*
                  THE CAVEAT BELONGS ON THE LEGEND ENTRY, not only on the rows.
                  The legend is where a reader decides how much to believe a
                  colour, and "observed by the Durable Object alone — THE
                  PROVIDER" is a claim about the instrument. The instrument only
                  measures the provider when the playout queue was empty, and
                  nothing in the words "response.created → output_audio_buffer.
                  started" says so.
                */}
                {s.key === 'inference' && <InferenceCaveat turns={turns} />}
                {measured === 0 ? (
                  <strong className="font-semibold text-ev-warn">
                    {' '}
                    Not measured on this session — no turn carries this interval, so none of
                    the bars above contain any of it.
                  </strong>
                ) : measured < turns.length ? (
                  <span className="faint">
                    {' '}
                    Present on {measured} of {turns.length} turns.
                  </span>
                ) : null}
              </dd>
            </div>
          )
        })}
      </dl>
      <p className="mt-3 max-w-3xl text-xs text-muted-foreground">
        <strong className="font-semibold text-foreground">Tool time is not additive.</strong>{' '}
        A tool runs inside the response window, so its segment overlaps inference rather
        than following it. It is drawn last so a slow tool is visible at all; the
        &ldquo;to first audio&rdquo; column is the stored per-turn figure and does not include
        it. Bars are drawn to a scale shared by every turn in this session, so a short bar
        is a genuinely fast turn rather than a rescaled one.
      </p>
    </div>
  )
}
